import styled, { keyframes, css } from "styled-components";
import Video from "../Video/Video";
import Image from "../Image/Image";

/* ─── Motion ─────────────────────────────────────────────────────────────── */

const dotBounce = keyframes`
  0%, 80%, 100% {
    opacity: 0.35;
    transform: translateY(0) scale(0.85);
  }
  40% {
    opacity: 1;
    transform: translateY(-2px) scale(1);
  }
`;

const sparkleWiggle = keyframes`
  0%, 100% { transform: translateY(0) rotate(-4deg); }
  50% { transform: translateY(-1px) rotate(6deg); }
`;

const favoritePop = keyframes`
  0% { transform: scale(0.4); }
  55% { transform: scale(1.22); }
  80% { transform: scale(0.92); }
  100% { transform: scale(1); }
`;

const favoriteBurst = keyframes`
  0% {
    opacity: 0.6;
    transform: scale(0.2);
  }
  70% {
    opacity: 0.35;
  }
  100% {
    opacity: 0;
    transform: scale(1.85);
  }
`;

/* ─── Modal shell — water-glass panel with outer aqua halo ───────────────── */

export const ModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 540px;
  /* Cap to viewport (overlay has 16px padding top+bottom) and let the modal
     scroll internally. Without this, tall content — e.g. the permission text
     wrapping to two lines on narrow phones — pushes the modal past the screen
     edges where it can't be reached, since the overlay itself doesn't scroll.
     Pass $disableOverflow to opt out (e.g. when an inner popover/tooltip needs
     to overflow the modal bounds). */
  ${({ $disableOverflow }) =>
    $disableOverflow
      ? css`
          overflow: visible;
        `
      : css`
          max-height: calc(100vh - 32px);
          max-height: calc(100dvh - 32px);
          overflow-y: auto;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
        `}
  padding: 0;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  /* Lower the shell blur (was 28px). bg above is 0.94-0.96 alpha so the
     blur was almost pure overhead. Brightness keyframe also dropped — it
     ran continuously while the modal was open, forcing a full raster
     pass per frame for an effect users couldn't really see. */
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 28px 56px -18px rgba(0, 0, 0, 0.6),
      0 10px 24px rgba(0, 0, 0, 0.4),
      inset 0 -10px 22px rgba(var(--color-brand-700-rgb), 0.18);
  }

  /* Top specular sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 20px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 10;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Bottom aqua underglow */
  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 25%;
    right: 25%;
    height: 16px;
    background: radial-gradient(
      ellipse at center top,
      rgba(var(--color-brand-400-rgb), 0.42) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: -1;
  }

  [data-theme="dark"] &::after {
    opacity: 0;
  }
`;

/* ─── Generate loader text (inline) ──────────────────────────────────────── */

export const GenerateLoader = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.01em;
`;

export const GenerateLoaderDots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;

  span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: currentColor;
    animation: ${dotBounce} 1.1s ease-in-out infinite;
  }

  span:nth-child(2) {
    animation-delay: 0.15s;
  }

  span:nth-child(3) {
    animation-delay: 0.3s;
  }
`;

export const GenerateLoaderSparkle = styled.span`
  display: inline-flex;
  font-size: 14px;
  line-height: normal;
  animation: ${sparkleWiggle} 1.4s ease-in-out infinite;
  transform-origin: center;
`;

/* ─── Header ─────────────────────────────────────────────────────────────── */

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 22px 4px;
  gap: 12px;
  position: relative;
  z-index: 2;
  @media (max-width: 768px) {
    padding: 20px 8px 4px;
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  gap: 6px;
`;

export const RowIcons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

export const IconsContainer = styled.div`
  display: flex;
  align-items: center;
`;

/* Frosted-glass circle close button (matches MenuButton language) */
export const IconWrapper = styled.div`
  position: relative;
  width: 36px;
  height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.92) 0%,
    rgba(var(--glass-white-rgb), 0.7) 45%,
    rgba(var(--glass-pale-rgb), 0.55) 100%
  );
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
  color: var(--color-brand-700);
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy);

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
  }

  /* Top-left sheen */
  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 5px;
    width: 16px;
    height: 9px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    width: 16px;
    height: 16px;
    position: relative;
    z-index: 1;
  }

  svg path,
  svg rect,
  svg circle,
  svg line {
    stroke: var(--color-brand-700);
    transition: stroke 180ms var(--ease-snappy);
  }

  [data-theme="dark"] & svg path,
  [data-theme="dark"] & svg rect,
  [data-theme="dark"] & svg circle,
  [data-theme="dark"] & svg line {
    stroke: var(--color-text-strong);
  }

  &:hover {
    transform: translateY(-1px) scale(1.04);
    border-color: rgba(var(--color-brand-400-rgb), 0.45);
  }
`;

/* Favorite heart button — same frosted circle base */
export const FavoriteIconWrapper = styled.button`
  position: relative;
  width: 36px;
  height: 36px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.92) 0%,
    rgba(var(--glass-white-rgb), 0.7) 45%,
    rgba(var(--glass-pale-rgb), 0.55) 100%
  );
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 5px;
    width: 16px;
    height: 9px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &:hover {
    transform: translateY(-1px) scale(1.04);
    border-color: rgba(var(--color-brand-400-rgb), 0.45);
  }

  &:active {
    transform: translateY(0) scale(0.94);
  }

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
  }
`;

export const FavoriteHeartLayer = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 2;

  svg {
    width: 18px;
    height: 18px;
    display: block;
  }
`;

export const FavoriteHeartOutline = styled(FavoriteHeartLayer)`
  opacity: ${(p) => (p.$active ? 0 : 1)};
  transform: ${(p) => (p.$active ? "scale(0.55)" : "scale(1)")};
  transition:
    opacity 200ms var(--ease-snappy),
    transform 220ms var(--ease-snappy);

  svg path {
    stroke: var(--color-brand-700);
    transition: stroke 180ms var(--ease-snappy);
  }

  [data-theme="dark"] & svg path {
    stroke: var(--color-brand-300);
  }
`;

export const FavoriteHeartFilled = styled(FavoriteHeartLayer)`
  opacity: ${(p) => (p.$active ? 1 : 0)};
  transform: ${(p) => (p.$active ? "scale(1)" : "scale(0.3)")};
  animation: ${(p) => (p.$active && p.$animate ? favoritePop : "none")} 440ms
    cubic-bezier(0.34, 1.56, 0.64, 1);
  transition:
    opacity 220ms var(--ease-snappy),
    transform 240ms var(--ease-snappy);

  svg path {
    fill: var(--color-brand-600);
    stroke: var(--color-brand-600);
    filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.3));
  }
`;

export const FavoriteBurst = styled.span`
  position: absolute;
  inset: 0;
  margin: auto;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  pointer-events: none;
  background: radial-gradient(
    circle,
    rgba(var(--color-brand-400-rgb), 0.55) 0%,
    rgba(var(--color-brand-600-rgb), 0.3) 45%,
    rgba(var(--color-brand-600-rgb), 0) 75%
  );
  opacity: 0;
  animation: ${(p) => (p.$trigger ? favoriteBurst : "none")} 520ms ease-out;
  z-index: 0;
`;

/* Brand-gradient model title */
export const Title = styled.div`
  flex: 1;
  min-width: 0;
  font-family: var(--font-family-primary);
  font-size: 22px;
  font-weight: 800;
  line-height: normal;
  letter-spacing: -0.03em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));
`;

/* ─── Tags ───────────────────────────────────────────────────────────────── */

export const CategoryContainer = styled.div`
  display: flex;
  gap: 6px;
  padding: 0px 22px 0;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
  @media (max-width: 768px) {
    padding: 0px 8px 0;
  }
`;

export const CategoryItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-width: ${({ $width }) => $width && `${$width}px`};
  justify-content: center;
  min-height: 24px;
  padding: 3px 12px;
  border-radius: 999px;
  border: 1px solid rgba(var(--color-brand-400-rgb), 0.28);
  background: linear-gradient(
    180deg,
    rgba(var(--glass-white-rgb), 0.92) 0%,
    rgba(var(--glass-pale-rgb), 0.78) 100%
  );
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    0 2px 4px rgba(var(--color-brand-500-rgb), 0.08);

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.22);
    background: rgba(var(--color-brand-700-rgb), 0.18);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.32);
  }
`;

export const CategoryText = styled.p`
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;

  [data-theme="dark"] & {
    color: var(--color-brand-300);
  }
`;

/* ─── Admin actions ──────────────────────────────────────────────────────── */

export const AdminActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding: 14px 22px 0;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
  @media (max-width: 768px) {
    padding: 14px 8px 0;
  }
`;

export const AdminActionButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.7);
  background: radial-gradient(
    120% 100% at 20% 0%,
    rgba(var(--glass-white-rgb), 0.95) 0%,
    rgba(var(--glass-pale-rgb), 0.78) 55%,
    rgba(var(--glass-pale-rgb), 0.65) 100%
  );
  box-shadow:
    0 4px 10px rgba(var(--color-brand-500-rgb), 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    inset 0 -2px 5px rgba(var(--color-brand-200-rgb), 0.18);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition:
    opacity 180ms var(--ease-snappy),
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy),
    box-shadow 180ms var(--ease-snappy);

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: rgba(var(--color-brand-400-rgb), 0.45);
    box-shadow:
      0 8px 16px rgba(var(--color-brand-500-rgb), 0.16),
      inset 0 1px 0 rgba(255, 255, 255, 1),
      inset 0 -2px 6px rgba(var(--color-brand-200-rgb), 0.28);
  }

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
    color: var(--color-brand-300);
  }
`;

/* ─── Tabs — clean underline indicator (matches TabsComponent) ──────────── */

export const TabsContainer = styled.div`
  display: flex;
  padding: 0;
  border-bottom: 1px solid rgba(var(--color-brand-300-rgb), 0.2);
  position: relative;
  z-index: 1;
`;

export const Tab = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 12px 8px 14px;
  opacity: ${({ $disabled }) => ($disabled ? 0.35 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  transition:
    opacity 180ms var(--ease-snappy),
    color 180ms var(--ease-snappy);

  svg {
    width: 16px;
    height: 16px;
    transition: color 200ms var(--ease-snappy);
    color: ${({ $selected }) =>
      $selected ? "var(--color-brand-600)" : "var(--color-text-muted)"};
    ${({ $selected }) =>
      $selected &&
      `filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.25));`}
  }

  svg path,
  svg rect,
  svg circle,
  svg line {
    stroke: ${({ $selected }) =>
      $selected ? "var(--color-brand-600)" : "var(--color-text-muted)"};
    transition: stroke 180ms var(--ease-snappy);
  }

  &:hover svg {
    color: ${({ $selected }) =>
      $selected ? "var(--color-brand-600)" : "var(--color-text-body)"};
  }

  /* Animated gradient underline */
  &::after {
    content: "";
    position: absolute;
    left: 20%;
    right: 20%;
    bottom: -1px;
    height: 3px;
    border-radius: 999px;
    background: linear-gradient(
      90deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 100%
    );
    box-shadow:
      0 2px 6px rgba(var(--color-brand-600-rgb), 0.35),
      0 1px 2px rgba(var(--color-brand-400-rgb), 0.25);
    opacity: ${({ $selected }) => ($selected ? 1 : 0)};
    transform: scaleX(${({ $selected }) => ($selected ? 1 : 0.4)});
    transform-origin: center;
    transition:
      opacity 260ms var(--ease-snappy),
      transform 320ms var(--ease-snappy);
  }
`;

export const TabText = styled.p`
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: ${({ $selected }) => ($selected ? 700 : 500)};
  line-height: normal;
  letter-spacing: -0.005em;
  white-space: nowrap;
  transition:
    color 220ms var(--ease-snappy),
    font-weight 220ms var(--ease-snappy);

  ${({ $selected }) =>
    $selected
      ? css`
          background: linear-gradient(
            180deg,
            var(--color-brand-400) 0%,
            var(--color-brand-600) 60%,
            var(--color-brand-700) 100%
          );
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        `
      : css`
          color: var(--color-text-muted);
        `}
`;

/* ─── Content area ───────────────────────────────────────────────────────── */

export const Content = styled.div`
  padding: 16px 22px 20px;
  display: flex;
  position: relative;
  z-index: 1;
  @media (max-width: 768px) {
    padding: 16px 8px 20px;
  }
`;

export const Container = styled.div`
  display: flex;
  gap: 14px;
  position: relative;
  min-height: 380px;
  width: 100%;
  @media (max-width: 768px) {
    height: unset;
    min-height: unset;
    gap: 10px;
  }
`;

/* ─── Preview (right column) ─────────────────────────────────────────────── */

export const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

export const SectionLabel = styled.span`
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const RightContent = styled.div`
  flex: 1;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.28);
  background: rgba(var(--glass-pale-rgb), 0.55);
  box-shadow:
    0 8px 18px rgba(var(--color-brand-500-rgb), 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.8),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
`;

export const StyledVideo = styled(Video)`
  height: 100%;
  width: unset;
  aspect-ratio: unset;
`;

export const StyledImage = styled(Image)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-color: ${(props) =>
    props.$hideImageContainer
      ? "transparent"
      : "rgba(var(--color-brand-300-rgb), 0.1)"};
  max-width: 100%;

  img {
    border-radius: 20px;
    width: auto;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
  }
`;

export const RightContentOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(var(--color-brand-900-rgb), 0) 60%,
    rgba(var(--color-brand-900-rgb), 0.34) 100%
  );
  pointer-events: none;
`;

/* ─── Generate bar ───────────────────────────────────────────────────────── */

export const EmptyDiv = styled.div`
  display: flex;
  height: 96px;
  @media (max-width: 768px) {
    display: none;
  }
`;

export const GenerateContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    display: none;
  }
`;

/* Legacy Generate button (kept for back-compat; actual CTA is ExplosionButton) */
export const GenerateButton = styled.button`
  width: 130px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: var(--radius-pill);
  border: 1px solid rgba(var(--glass-white-rgb), 0.8);
  background:
    radial-gradient(
      140% 100% at 20% 0%,
      rgba(255, 255, 255, 0.32) 0%,
      rgba(255, 255, 255, 0) 55%
    ),
    linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 55%,
      var(--color-brand-700) 100%
    );
  box-shadow:
    0 14px 28px rgba(var(--color-brand-600-rgb), 0.38),
    inset 0 1px 0 rgba(255, 255, 255, 0.6),
    inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.32);
  outline: none;
  cursor: pointer;
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.005em;
  padding-inline: 8px;
  flex: 1;

  &:disabled {
    cursor: not-allowed;
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding-inline: 4px;
    width: unset;
    font-size: 13px;
    height: 38px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* Frosted circle icon button (settings, info) */
export const StyledIconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  min-width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.92) 0%,
    rgba(var(--glass-white-rgb), 0.7) 45%,
    rgba(var(--glass-pale-rgb), 0.55) 100%
  );
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
  color: var(--color-brand-700);
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 6px;
    width: 18px;
    height: 10px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }
  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    transform: translateY(-1px) scale(1.04);
    border-color: rgba(var(--color-brand-400-rgb), 0.45);
  }

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
    color: var(--color-brand-300);
  }
`;

export const InfoIconWrapper = styled(StyledIconWrapper)`
  svg {
    position: relative;
    z-index: 1;
    path {
      stroke: var(--color-brand-600);
    }
  }

  [data-theme="dark"] & svg path {
    stroke: var(--color-brand-300);
  }
`;

/* Frosted-glass tooltip (replaces dark panel) */
export const TooltipContainer = styled.div`
  position: absolute;
  bottom: 56px;
  left: 0;
  width: 340px;
  padding: 16px;
  border-radius: 18px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.95) 0%,
      rgba(var(--glass-pale-rgb), 0.85) 60%,
      rgba(var(--glass-pale-rgb), 0.75) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.92) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  /* Secondary panel — same blur reduction logic as ModalContainer. */
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  box-shadow:
    0 24px 48px -14px rgba(var(--color-brand-500-rgb), 0.36),
    0 8px 20px rgba(var(--color-brand-400-rgb), 0.18),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -6px 14px rgba(var(--color-brand-200-rgb), 0.22);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 100;

  /* Top sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 20px;
    right: 20px;
    height: 14px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  @media (max-width: 768px) {
    width: 280px;
    left: -245px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 24px 48px -14px rgba(0, 0, 0, 0.6),
      0 8px 20px rgba(0, 0, 0, 0.4),
      inset 0 -6px 14px rgba(var(--color-brand-700-rgb), 0.18);
  }
`;

export const TooltipText = styled.p`
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.005em;
  color: var(--color-text-body);
  position: relative;
  z-index: 1;

  ${(p) =>
    p.$light &&
    css`
      font-weight: 700;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--color-brand-700);
    `}
`;

/* ─── Mobile options ─────────────────────────────────────────────────────── */

export const NudifyText = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.005em;
`;

export const MobileToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 180ms var(--ease-snappy);

  &:hover {
    background: rgba(var(--color-brand-300-rgb), 0.12);
  }
`;

export const MobileToggleTrack = styled.div`
  position: relative;
  width: 40px;
  height: 22px;
  border-radius: 11px;
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 100%)"
      : "rgba(var(--color-brand-300-rgb), 0.2)"};
  border: 1px solid
    ${({ $active }) =>
      $active
        ? "rgba(var(--glass-white-rgb), 0.7)"
        : "rgba(var(--color-brand-300-rgb), 0.3)"};
  box-shadow: ${({ $active }) =>
    $active
      ? `0 4px 10px rgba(var(--color-brand-600-rgb), 0.3),
         inset 0 1px 0 rgba(255, 255, 255, 0.55)`
      : `inset 0 1px 2px rgba(var(--color-brand-300-rgb), 0.18)`};
  cursor: pointer;
  transition: all 220ms var(--ease-snappy);
  flex-shrink: 0;

  [data-theme="dark"] & {
    background: ${({ $active }) =>
      $active
        ? "linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 100%)"
        : "rgba(var(--color-brand-900-rgb), 0.6)"};
    border-color: ${({ $active }) =>
      $active ? "transparent" : "rgba(var(--color-brand-400-rgb), 0.32)"};
    box-shadow: ${({ $active }) =>
      $active
        ? "0 4px 10px rgba(0, 0, 0, 0.5)"
        : "inset 0 1px 2px rgba(0, 0, 0, 0.4)"};
  }
`;

export const MobileToggleThumb = styled.div`
  position: absolute;
  top: 2px;
  left: ${({ $active }) => ($active ? "20px" : "2px")};
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ $active }) =>
    $active ? "#ffffff" : "rgba(var(--glass-white-rgb), 0.9)"};
  box-shadow:
    0 2px 4px rgba(var(--color-brand-900-rgb), 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transition: left 220ms var(--ease-snappy);

  [data-theme="dark"] & {
    background: ${({ $active }) =>
      $active ? "#ffffff" : "rgba(var(--color-brand-300-rgb), 0.7)"};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }
`;

export const MobileCheckboxRow = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 0 8px;
    padding: 4px 0;
    border-radius: 14px;
    overflow: hidden;
    border: 1px solid rgba(var(--color-brand-300-rgb), 0.2);
    background: var(--glass-base-2);
    visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
    display: ${({ $visible }) => ($visible ? "flex" : "none")};
    pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  }
`;

export const GenerateContainerMobile = styled.div`
  align-items: center;
  display: none;
  gap: 8px;
  width: 100%;
  min-width: 0;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    display: flex;
    padding: 10px 8px 18px;
  }
`;

const confirmShake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-3px); }
  80% { transform: translateX(3px); }
`;

export const ConfirmPermissionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  padding: 12px 20px 16px;
  border-radius: 0 0 28px 28px;
  border-top: 1px solid rgba(var(--color-brand-300-rgb), 0.22);
  background: var(--glass-base-2);
  cursor: pointer;
  position: relative;
  z-index: 1;
  transition: background 180ms var(--ease-snappy);
  animation: ${({ $error }) => ($error ? confirmShake : "none")} 0.4s ease;

  &:hover {
    background: rgba(var(--color-brand-300-rgb), 0.12);
  }

  [data-theme="dark"] & {
    border-top-color: rgba(var(--color-brand-400-rgb), 0.24);
    background: rgba(var(--color-brand-900-rgb), 0.4);
  }
`;

export const ConfirmPermissionText = styled.span`
  flex: 1;
  min-width: 0;
  word-break: break-word;
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.005em;
  line-height: 1.3;
  color: ${({ $checked, $error }) =>
    $error
      ? "var(--color-danger, #e5484d)"
      : $checked
        ? "var(--color-brand-600)"
        : "var(--color-text-strong)"};
`;
